/**
 * Login Page (Story 1.3, 1.7)
 * Handles email/password login and 2FA verification.
 */
import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { useForm } from "react-hook-form";
import { useAuthStore } from "../store/authStore";
import { authService } from "../services/authService";
import { twoFactorService } from "../services/twoFactorService";
import { Button } from "../components/atoms/Button";
import { Alert } from "../components/atoms/Alert";
import { TwoFactorVerify } from "../components/organisms/TwoFactorVerify";
import { TwoFactorLoginPrompt } from "../components/molecules/TwoFactorLoginPrompt";
import logoImage from "../assets/global-clean-logo.png";
import { LanguageSwitcher } from "../components/molecules/LanguageSwitcher";

interface LoginFormData {
  identifier: string;
  password: string;
}

interface LocationState {
  from?: {
    pathname: string;
  };
  /** Set after successful change-password from profile; session was cleared. */
  passwordChanged?: boolean;
}

export function Login() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    login,
    setPendingTwoFactor,
    completeTwoFactor,
    clearPendingTwoFactor,
    pendingTwoFactor,
    twoFactorSessionToken,
    setGracePeriod,
    setTotpDisabledByAdmin,
  } = useAuthStore();

  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);
  const [showGracePrompt, setShowGracePrompt] = useState(false);
  const [gracePromptDays, setGracePromptDays] = useState<number>(0);
  const [pendingRedirect, setPendingRedirect] = useState<string | null>(null);
  const [passwordChangedBanner] = useState(
    () => (location.state as LocationState | undefined)?.passwordChanged === true
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    // Don't clear error immediately - let user see it
    // Only clear error when starting a new attempt (not on rate limit errors)
    setIsLoading(true);

    try {
      const result = await authService.login(data.identifier, data.password);

      // Clear error only on successful login attempt (not necessarily success)
      setError("");

      if (result.requires2FA && result.sessionToken) {
        // 2FA required - show 2FA form
        setPendingTwoFactor(result.sessionToken);
      } else if (result.success && result.token && result.user) {
        // Login succeeded
        login(result.token, result.user);

        // Staff: check grace period first (Story 1-7, 10-5-8)
        if (result.user.role === "staff") {
          if (
            result.gracePeriodStarted &&
            typeof result.graceDaysRemaining === "number" &&
            result.graceDaysRemaining > 0
          ) {
            setTotpDisabledByAdmin(!!result.totpDisabledByAdmin);
            setGracePeriod(true, result.graceDaysRemaining);
            setGracePromptDays(result.graceDaysRemaining);
            setPendingRedirect("/staff");
            setShowGracePrompt(true);
          } else if (result.gracePeriodStarted && result.graceDaysRemaining === 0) {
            // Grace period expired — force 2FA setup (Story 10-5-10)
            setGracePeriod(true, 0);
            navigate("/setup-2fa-required", { replace: true });
          } else {
            navigate("/staff", { replace: true });
          }
          return;
        }

        // Investor always go to /investor (Story 9-3)
        if (result.user.role === "investor") {
          if (
            result.gracePeriodStarted &&
            typeof result.graceDaysRemaining === "number" &&
            result.graceDaysRemaining > 0
          ) {
            setTotpDisabledByAdmin(!!result.totpDisabledByAdmin);
            setGracePeriod(true, result.graceDaysRemaining);
            setGracePromptDays(result.graceDaysRemaining);
            setPendingRedirect("/investor");
            setShowGracePrompt(true);
          } else if (result.gracePeriodStarted && result.graceDaysRemaining === 0) {
            // Grace period expired — force 2FA setup (Story 10-5-10)
            setGracePeriod(true, 0);
            navigate("/setup-2fa-required", { replace: true });
          } else {
            navigate("/investor", { replace: true });
          }
          return;
        }

        const locationState = location.state as LocationState;
        const from = locationState?.from?.pathname || "/";

        // Set grace period and admin recovery banner (Story 6-6-9)
        if (result.totpDisabledByAdmin) {
          setTotpDisabledByAdmin(true);
        }
        if (
          result.gracePeriodStarted &&
          typeof result.graceDaysRemaining === "number" &&
          result.graceDaysRemaining > 0
        ) {
          setGracePeriod(true, result.graceDaysRemaining);
          setGracePromptDays(result.graceDaysRemaining);
          setPendingRedirect(from);
          setShowGracePrompt(true);
        } else if (result.gracePeriodStarted && result.graceDaysRemaining === 0) {
          // Grace period expired — force 2FA setup (Story 10-5-10)
          setGracePeriod(true, 0);
          navigate("/setup-2fa-required", { replace: true });
        } else {
          navigate(from, { replace: true });
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t("login_failed");
      setError(errorMessage);
      // Keep error visible - don't clear it automatically
      // Error will persist until user tries again or manually dismisses
    } finally {
      setIsLoading(false);
    }
  };

  const handleTwoFactorVerify = async (code: string, useBackup: boolean) => {
    if (!twoFactorSessionToken) {
      setTwoFactorError(t("login_session_expired"));
      clearPendingTwoFactor();
      return;
    }

    setTwoFactorError(null);
    setIsLoading(true);

    try {
      const token = await twoFactorService.submitTwoFactorCode(
        code,
        twoFactorSessionToken,
        useBackup
      );

      await completeTwoFactor(token);

      // Staff redirect to /staff after 2FA (Story 8-1)
      const { user: currentUser } = useAuthStore.getState();
      if (currentUser?.role === "staff") {
        navigate("/staff", { replace: true });
        return;
      }

      // Investor redirect to /investor after 2FA (Story 9-3)
      if (currentUser?.role === "investor") {
        navigate("/investor", { replace: true });
        return;
      }

      const locationState = location.state as LocationState;
      const from = locationState?.from?.pathname || "/";
      navigate(from, { replace: true });
    } catch (err) {
      setTwoFactorError(err instanceof Error ? err.message : t("login_verification_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleTwoFactorCancel = () => {
    clearPendingTwoFactor();
    setTwoFactorError(null);
  };

  const handleGracePromptSetup = () => {
    // Navigate to security settings to set up 2FA (role-aware: investor uses /investor/settings/security)
    const securityPath =
      user?.role === "investor" ? "/investor/settings/security" : "/settings/security";
    navigate(securityPath, { replace: true });
  };

  const handleGracePromptRemindLater = () => {
    // Dismiss prompt and navigate to original destination
    setShowGracePrompt(false);
    if (pendingRedirect) {
      navigate(pendingRedirect, { replace: true });
    } else {
      navigate("/", { replace: true });
    }
  };

  // Show grace period prompt
  if (showGracePrompt) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-900 px-4">
        <div className="max-w-md w-full">
          <TwoFactorLoginPrompt
            daysRemaining={gracePromptDays}
            onSetupNow={handleGracePromptSetup}
            onRemindLater={handleGracePromptRemindLater}
          />
        </div>
      </div>
    );
  }

  // Show 2FA verification form
  if (pendingTwoFactor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-900 px-4">
        <div className="max-w-md w-full">
          <TwoFactorVerify
            onVerify={handleTwoFactorVerify}
            onCancel={handleTwoFactorCancel}
            loading={isLoading}
            error={twoFactorError}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-900 px-4 relative">
      <div className="absolute top-4 end-4 z-50">
        <LanguageSwitcher />
      </div>
      <div className="max-w-md w-full space-y-8">
        <div className="flex flex-col items-center">
          <img src={logoImage} alt={t("login_logo_alt")} className="h-24 w-auto mb-4" />
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-neutral-400">
            {t("login_title")}
          </p>
        </div>

        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-800 dark:text-amber-200">
          <p className="m-0 leading-relaxed">
            <Trans
              ns="auth"
              i18nKey="login_customer_portal_banner"
              values={{ phone: t("login_phone_display") }}
              components={{
                requestLink: (
                  <Link
                    to="/portal/request-access"
                    className="font-medium text-amber-900 underline underline-offset-2 hover:text-amber-950 dark:text-amber-100 dark:hover:text-white"
                  />
                ),
                phoneLink: (
                  <a
                    href="tel:+213795318400"
                    className="font-medium text-amber-900 underline underline-offset-2 whitespace-nowrap dark:text-amber-100 dark:hover:text-white"
                  />
                ),
              }}
            />
          </p>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {passwordChangedBanner && (
            <Alert variant="success">
              <span>{t("login_password_changed")}</span>
            </Alert>
          )}
          {error && (
            <Alert variant="error">
              <div className="flex items-start justify-between gap-2">
                <span>{error}</span>
                <button
                  type="button"
                  onClick={() => setError("")}
                  className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 flex-shrink-0"
                  aria-label={t("login_dismiss_error")}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </Alert>
          )}

          <div className="space-y-4">
            <div>
              <label
                htmlFor="identifier"
                className="block text-sm font-medium text-gray-700 dark:text-neutral-300"
              >
                {t("login_identifier_label")}
              </label>
              <input
                {...register("identifier", {
                  required: t("login_identifier_required"),
                  minLength: { value: 1, message: t("login_identifier_required") },
                })}
                type="text"
                autoComplete="username"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                placeholder="admin@globalclean.org"
              />
              {errors.identifier && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.identifier.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 dark:text-neutral-300"
              >
                {t("login_password_label")}
              </label>
              <div className="relative">
                <input
                  {...register("password", {
                    required: t("login_password_required"),
                    minLength: {
                      value: 10,
                      message: t("login_password_min_length"),
                    },
                  })}
                  type={showPassword ? "text" : "password"}
                  className="mt-1 block w-full px-3 py-2 pe-10 border border-gray-300 dark:border-neutral-600 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 end-0 flex items-center pe-3 text-gray-400 hover:text-gray-600 dark:text-neutral-500 dark:hover:text-neutral-300"
                  aria-label={showPassword ? t("login_hide_password") : t("login_show_password")}
                >
                  {showPassword ? (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                      />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                {t("login_forgot_password")}
              </Link>
            </div>
          </div>

          <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isLoading}>
            {isLoading ? t("login_signing_in") : t("login_submit")}
          </Button>
        </form>
      </div>
    </div>
  );
}
